import { useCallback } from 'react';
import { useNodeActions, type PreparedDelete } from '@/components/Nodes/useNodeActions';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useWorkflowStore } from '@/state/workflow.state';
import { useFlowView, useGetEdges, useGetNodes } from '../FlowContext';
import { buildNodeClipboardPayload } from '../nodeClipboard';
import type { NodeId } from 'web';

export type CutNodesSource = 'keyboard' | 'context_menu' | 'node_menu';

/**
 * Starts a clipboard write during the confirm click and fills it only when `commit` runs.
 * `abort` rejects the write, so the previous clipboard stays. A later `writeText` would be
 * rejected once the config request has awaited.
 */
function beginCutClipboardWrite(payload: string): PreparedDelete {
  const settle: Partial<PreparedDelete> = {};
  const text = new Promise<Blob>((resolve, reject) => {
    settle.commit = () => resolve(new Blob([payload], { type: 'text/plain' }));
    settle.abort = () => reject(new Error('cut did not complete'));
  });
  void navigator.clipboard.write([new ClipboardItem({ 'text/plain': text })]).catch(() => undefined);
  return {
    commit: () => settle.commit?.(),
    abort: () => settle.abort?.(),
  };
}

export type CopyNodesOptions = { deleteAfterCopy: true; source: CutNodesSource } | { deleteAfterCopy?: false };

export function useCopyNodesToClipboard(): (nodeIds: NodeId[], options?: CopyNodesOptions) => void {
  const workflowId = useWorkflowStore((state) => state.recipe.id);
  const flowView = useFlowView();
  const getNodes = useGetNodes();
  const getEdges = useGetEdges();
  const { deleteNodes } = useNodeActions();
  const { track } = useAnalytics();

  return useCallback(
    (nodeIds: NodeId[], options?: CopyNodesOptions) => {
      // Build the payload before any delete. Cancel never starts a clipboard write.
      const payload = buildNodeClipboardPayload(
        nodeIds,
        getNodes(),
        getEdges(),
        (groupId) => flowView.getChildNodesOfGroup(groupId),
        workflowId,
      );
      if (!payload) return;

      if (!options?.deleteAfterCopy) {
        void navigator.clipboard.writeText(payload);
        return;
      }

      track('copy_nodes', { nodeCount: nodeIds.length, source: options.source, isCut: true });
      deleteNodes(nodeIds, true, () => beginCutClipboardWrite(payload));
    },
    [deleteNodes, flowView, getEdges, getNodes, track, workflowId],
  );
}
